/**
 * validateTransformPipeline.js
 *
 * Structural validation for a value-transform pipeline (see
 * valueTransforms.js) — purely local, static analysis of the pipeline
 * itself. Kept separate from valueTransforms.js's execution
 * (runTransformPipeline) for the same reason evaluateMappingRules.js and
 * validateMappingRules.js are separate: one runs a value through the
 * pipeline, this one checks the pipeline's own structure. Never mutates
 * its input, never blocks editing — every issue here is a warning.
 *
 * @typedef {{ code: string, severity: 'warning', message: string, stepIndex: number }} TransformIssue
 */

import { TRANSFORM_TYPES } from './valueTransforms.js';

/** @private — a numeric param that's missing or not a finite number. */
function checkInvalidNumericParams(pipeline) {
  const issues = [];
  pipeline.forEach((step, i) => {
    const meta = TRANSFORM_TYPES[step.type];
    if (!meta) return;
    for (const paramDef of meta.params) {
      if (!paramDef.numeric) continue;
      const raw = step.params?.[paramDef.key];
      if (raw === undefined || raw === '' || !Number.isFinite(Number(raw))) {
        issues.push({
          code: 'invalid-numeric-param',
          severity: 'warning',
          stepIndex: i,
          message: `"${meta.label}" — ${paramDef.label} must be a number.`,
        });
      }
    }
  });
  return issues;
}

/** @private — a 'divide' step whose divisor is (or resolves to) zero. */
function checkDivideByZero(pipeline) {
  return pipeline
    .map((step, i) => ({ step, i }))
    .filter(({ step }) => step.type === 'divide' && Number(step.params?.divisor) === 0)
    .map(({ i }) => ({
      code: 'divide-by-zero',
      severity: 'warning',
      stepIndex: i,
      message: 'Dividing by zero always produces Infinity or NaN.',
    }));
}

/**
 * @private — a numeric transform (other than 'integer'/'float' themselves)
 * reached before any numeric conversion has happened yet, i.e. it would
 * run on a value still in its raw incoming (string) form.
 */
function checkIncompatibleSequence(pipeline) {
  const issues = [];
  let isNumeric = false;

  pipeline.forEach((step, i) => {
    const meta = TRANSFORM_TYPES[step.type];
    if (!meta) return;

    if (meta.numeric && !isNumeric && !meta.converts) {
      issues.push({
        code: 'incompatible-sequence',
        severity: 'warning',
        stepIndex: i,
        message: `"${meta.label}" expects a number, but the value hasn't been converted yet — add an "Integer" or "Float" step before it.`,
      });
    }

    if (meta.numeric) isNumeric = true;
  });

  return issues;
}

// Registry — add a new check function here (and to this array) to extend
// validation; mirrors validateMappingRules.js's CHECKS pattern.
const CHECKS = [
  checkInvalidNumericParams,
  checkDivideByZero,
  checkIncompatibleSequence,
];

/**
 * Validate a transform pipeline. Read-only — never mutates `pipeline`.
 * Every issue is a warning; nothing here blocks editing.
 *
 * @param {Array<{type: string, params: Object}>} [pipeline]
 * @returns {TransformIssue[]}
 */
export function validateTransformPipeline(pipeline) {
  const safePipeline = pipeline ?? [];
  return CHECKS.flatMap(check => check(safePipeline));
}
